import { Archive, Pencil } from 'lucide-react';
import { apiErrorMessage, type ApiErrorInput } from '~/lib/apiError';
import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { LocalTime } from '~/components/common/LocalTime';
import { ListToolbar } from '~/components/layout';
import {
  Button,
  Card,
  CardBody,
  ConfirmModal,
  DataTable,
  EmptyState,
  Pagination,
  RowActionsMenu,
  StatusChip,
  type DataTableColumn,
} from '~/components/ui';
import { useListUrlState } from '~/hooks/useListUrlState';
import { matchesFlagSearch } from '~/lib/flagFilters';
import { clampPage, pageCount } from '~/lib/listParams';
import { useArchiveFlagMutation, useListFlagsQuery } from '~/modules/query/featureFlags';

import {
  FLAG_STATUS_LABELS,
  FLAG_STATUS_TONES,
  flagStatus,
} from '~/components/Constants/featureFlagsConstants';
import { errorText } from '~/components/pages/feature-flags/common';
import type { Reference } from '~/components/pages/feature-flags/common';
import type { Flag } from '~/components/pages/feature-flags/types';
import { routes } from '~/app/routes';

export const FlagsTab = ({ reference }: { reference: Reference }) => {
  const list = useListUrlState();
  const flags = useListFlagsQuery();
  const [archiveFlag] = useArchiveFlagMutation();
  const [archiving, setArchiving] = useState<Flag | null>(null);
  const { environments } = reference;

  const rows = useMemo(
    () => (flags.data ?? []).filter((f) => matchesFlagSearch(f, list.q)),
    [flags.data, list.q],
  );
  const pages = pageCount(rows.length, list.pageSize);
  const page = clampPage(list.page, rows.length, list.pageSize);
  const pageRows = rows.slice((page - 1) * list.pageSize, page * list.pageSize);

  const archive = (flag: Flag) => {
    archiveFlag(flag.key)
      .unwrap()
      .then(() => toast.success('Flag archived'))
      .catch((err) => toast.error(errorText(err, 'The flag could not be archived. Try again.')));
  };

  const columns: DataTableColumn<Flag>[] = [
    {
      id: 'flag',
      header: 'Flag',
      cell: (f) => (
        <div className="max-w-md">
          <p className="font-medium text-gray-800 dark:text-white/90">{f.key}</p>
          {f.description && (
            <p className="truncate text-xs text-gray-500 dark:text-gray-400" title={f.description}>
              {f.description}
            </p>
          )}
        </div>
      ),
    },
    {
      id: 'products',
      header: 'Products',
      cell: (f) =>
        f.products.length ? (
          f.products.join(', ')
        ) : (
          <span className="text-gray-400 dark:text-gray-500">—</span>
        ),
    },
    ...environments.map<DataTableColumn<Flag>>((env) => ({
      id: `env-${env.key}`,
      header: env.name,
      cell: (f) => {
        const c = f.envs[env.key];
        const status = c ? flagStatus(c) : 'disabled';
        return (
          <StatusChip label={FLAG_STATUS_LABELS[status]} tone={FLAG_STATUS_TONES[status]} dot />
        );
      },
    })),
    { id: 'updated', header: 'Updated', cell: (f) => <LocalTime value={f.updatedAt} /> },
    {
      id: 'actions',
      header: 'Actions',
      cell: (f) => (
        <RowActionsMenu
          testId={`feature-flags-flags-actions-${f.key}`}
          items={[
            {
              label: 'Edit',
              testId: 'edit',
              icon: <Pencil className="size-4" aria-hidden="true" />,
              href: routes.featureFlagsRecord('flags', f.key),
            },
            {
              label: 'Archive',
              testId: 'archive',
              icon: <Archive className="size-4" aria-hidden="true" />,
              onClick: () => setArchiving(f),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      <Card>
        <ListToolbar
          title="Flags"
          search={{
            value: list.q,
            onChange: list.setQ,
            placeholder: 'Search flags…',
            testId: 'feature-flags-flags-search',
          }}
        />
        {reference.environments.length === 0 && flags.data ? (
          <CardBody>
            <EmptyState
              title="Add an environment first"
              description="Flags keep separate settings in each environment. Add one before creating flags."
              action={
                <Button variant="outline" href={routes.featureFlagsAdmin('environments')}>
                  Go to environments
                </Button>
              }
            />
          </CardBody>
        ) : (
          <>
            <CardBody className="p-0">
              <DataTable<Flag>
                framed={false}
                testId="feature-flags-flags-table"
                loading={!flags.data && !flags.error}
                refreshing={flags.isFetching && !!flags.data}
                error={
                  flags.error && !flags.data
                    ? {
                        what: 'the flags',
                        message: apiErrorMessage(flags.error as ApiErrorInput),
                        onRetry: flags.refetch,
                      }
                    : undefined
                }
                emptyMessage={list.q ? 'No flags match.' : 'No flags yet.'}
                emptyDescription={
                  list.q
                    ? 'Clear the search or try another term.'
                    : 'Create a flag to control availability and target an audience.'
                }
                data={pageRows}
                rowKey={(f) => f.key}
                href={(f) => routes.featureFlagsRecord('flags', f.key)}
                rowLabel={(f) => `Open ${f.key}`}
                columns={columns}
              />
            </CardBody>
            <Pagination
              pageIndex={page - 1}
              pageSize={list.pageSize}
              pageCount={pages}
              totalRows={rows.length}
              onPageChange={(index) => list.setPage(index + 1)}
              pageSizeControl={{
                value: list.pageSize,
                onChange: list.setPageSize,
                options: [...list.pageSizes],
              }}
            />
          </>
        )}
      </Card>

      <ConfirmModal
        isOpen={!!archiving}
        title="Archive flag"
        message={`Archive '${archiving?.key}'? The flag stops being returned to your apps. Each check falls back to its in-code default.`}
        confirmLabel="Archive flag"
        testId="feature-flags-flags-archive"
        onClose={() => setArchiving(null)}
        onConfirm={() => {
          if (archiving) archive(archiving);
          setArchiving(null);
        }}
      />
    </>
  );
};

export default FlagsTab;
